import { RefObject, useEffect } from 'react';

const interceptEvent = (e: KeyboardEvent) => {
  e.stopPropagation();
  e.preventDefault();
};

interface UseSearchKeybindingsArgs {
  isEnabled: boolean;
  onEnter: () => void;
  onEscape: () => void;
  onArrowDown: () => void;
  onArrowUp: () => void;
  searchRef: RefObject<HTMLDivElement>;
  onClickOutside: () => void;
}

// Dropdown keybindings for submit (enter) and navigation (up/down/escape):
export const useSearchKeybindings = ({
  isEnabled,
  onEnter,
  onEscape,
  onArrowDown,
  onArrowUp,
  searchRef,
  onClickOutside,
}: UseSearchKeybindingsArgs) => {
  useEffect(() => {
    const onKeyDownEvent = (e: KeyboardEvent) => {
      if (!isEnabled) return;

      if (['Enter'].includes(e.key)) {
        interceptEvent(e);
        onEnter();
      }

      if (['Escape'].includes(e.key)) {
        interceptEvent(e);
        onEscape();
      }

      if (e.key === 'ArrowDown') {
        interceptEvent(e);
        onArrowDown();
      }

      if (e.key === 'ArrowUp') {
        interceptEvent(e);
        onArrowUp();
      }
    };

    document.addEventListener('keydown', onKeyDownEvent);

    return () => {
      document.removeEventListener('keydown', onKeyDownEvent);
    };
  }, [isEnabled, onEnter, onEscape, onArrowDown, onArrowUp]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        event.target &&
        !searchRef.current.contains(event.target as Node)
      ) {
        onClickOutside();
      }
    };

    if (isEnabled) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEnabled, onClickOutside]);
};
